import type { ProductSlugs } from '@deque/billing-utils';
import { apiRequest } from '../../../api-client';
import { type SupportedIntegrationProductSlugs } from '../../../constants';

export interface IntegrationConnection {
  id: string;
  product_slug:
    | SupportedIntegrationProductSlugs
    | ProductSlugs.axeAuditorIntegration;
  credential_id: string | null;
  base_url: string | null;
  enterprise_id: string;
  connected_user_id: string;
  status: 'connected' | 'disconnected' | 'deleted';
  public_key: string | null;
  consumer_key: string | null;
  created_at: string;
  updated_at: string;
  name: string | null;
  instance_id: string | null;
  ignore_ssl: boolean;
}

interface RequestBody {
  enterpriseId: string;
  token: string;
}

interface RequestByIdBody extends RequestBody {
  connectionId: string;
}

export interface ReconnectBody {
  credential_id?: string;
  base_url: string;
}

export interface UpdateBody {
  base_url?: string;
  consumer_key?: string;
  secret?: string;
  name?: string;
  ignore_ssl?: boolean;
}

export interface CreateBody extends Omit<ReconnectBody, 'base_url'> {
  product_slug:
    | SupportedIntegrationProductSlugs
    | ProductSlugs.axeAuditorIntegration;
  base_url?: string;
  name?: string;
  secret?: string;
  status?: 'connected' | 'disconnected' | 'deleted';
}

interface CreateIntegrationConnection extends RequestBody {
  body: CreateBody;
}

interface ReconnectIntegrationConnection extends RequestByIdBody {
  body: ReconnectBody;
}

interface UpdateIntegrationConnection extends RequestByIdBody {
  body: UpdateBody;
}
interface IntegrationConnectionRequest {
  enterpriseId: string;
  token: string;
  path?: string;
  method?: string;
  body?: CreateBody | ReconnectBody | UpdateBody | { status: string };
}

const integrationConnectionRequest = <T,>({
  enterpriseId,
  token,
  path = '',
  method,
  body
}: IntegrationConnectionRequest): Promise<T> => {
  return apiRequest<T>({
    endpoint: `/api/integrations/enterprises/${enterpriseId}/connections${path}`,
    method,
    body,
    token
  });
};

export const getIntegrationConnections = ({
  enterpriseId,
  token
}: {
  enterpriseId: string;
  token: string;
}) => {
  return integrationConnectionRequest<IntegrationConnection[]>({
    enterpriseId,
    token
  });
};

export const getIntegrationConnectionById = ({
  enterpriseId,
  connectionId,
  token
}: RequestByIdBody) => {
  return integrationConnectionRequest<IntegrationConnection>({
    enterpriseId,
    path: `/${connectionId}`,
    token
  });
};

export const createIntegrationConnection = ({
  enterpriseId,
  token,
  body
}: CreateIntegrationConnection) => {
  return integrationConnectionRequest<IntegrationConnection>({
    enterpriseId,
    method: 'POST',
    body,
    token
  });
};

export interface KeyInfo {
  public_key: string | null;
}

export const createKeyPair = ({
  enterpriseId,
  connectionId,
  token
}: {
  enterpriseId: string;
  connectionId: string;
  token: string;
}) => {
  return integrationConnectionRequest<IntegrationConnection>({
    path: `/${connectionId}/keypair`,
    enterpriseId,
    method: 'POST',
    token
  });
};

export const reconnectIntegrationConnection = ({
  enterpriseId,
  connectionId,
  token,
  body
}: ReconnectIntegrationConnection) => {
  return integrationConnectionRequest<IntegrationConnection>({
    enterpriseId,
    path: `/${connectionId}`,
    method: 'PUT',
    body: { ...body, status: 'connected' },
    token
  });
};

export const updateIntegrationConnection = ({
  enterpriseId,
  connectionId,
  token,
  body
}: UpdateIntegrationConnection) => {
  return integrationConnectionRequest<IntegrationConnection>({
    enterpriseId,
    path: `/${connectionId}`,
    method: 'PUT',
    body: { ...body },
    token
  });
};

export const disconnectIntegrationConnection = ({
  enterpriseId,
  connectionId,
  token
}: RequestByIdBody) => {
  return integrationConnectionRequest<IntegrationConnection>({
    enterpriseId,
    path: `/${connectionId}`,
    method: 'PUT',
    body: { status: 'disconnected' },
    token
  });
};

export const deleteIntegrationConnection = ({
  enterpriseId,
  connectionId,
  token
}: RequestByIdBody) => {
  return integrationConnectionRequest<IntegrationConnection>({
    enterpriseId,
    path: `/${connectionId}`,
    method: 'DELETE',
    token
  });
};
